import type { MouseEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { PlusOutlined, CloseOutlined, PaperClipOutlined, DeleteOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Radio } from 'antd';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetCitiesDataQuery } from '@/api/endpoints/cities';
import { useGetLMSAccountsDataQuery, useGetQuizzesDataQuery } from '@/api/endpoints/lmsData';
import type { ISchoolMainData } from '@/api/endpoints/schools';
import { useUpdateSchoolDataMutation } from '@/api/endpoints/schools';
import {
  DataSection,
  ImageUploadField,
  InputField,
  NavigationLink,
  SelectField,
  TextAreaField,
} from '@/shared/components';
import { CheckboxField } from '@/shared/components/Fields/CheckboxField';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';
import { EQuizType, ESchoolBadgeIcons, ESchoolFormat } from '@/shared/types';

import { IconSelectLabel } from '../_components/IconSelectLabel/IconSelectLabel';
import {
  SCHOOL_FORMAT_RADIO_GROUP_DATA,
  SHORT_DESC_INDEX_TO_FIELD_NAME_MAPPER,
  PUBLIC_SCHOOL_VALIDATION_SCHEMA,
} from '../consts';

import styles from './SchoolEditPage.module.scss';
import type { TPublicSchoolFormData } from './types';

interface IProps {
  schoolData: ISchoolMainData;
}

export const EditPublicSchoolComponent = ({ schoolData }: IProps) => {
  const sendApiRequest = useApiRequest();

  const navigate = useNavigate();

  const { data: citiesData = [], isLoading: areCitiesLoading } = useGetCitiesDataQuery();
  const { data: lmsAccountsData = [], isLoading: areLMSAccountsLoading } = useGetLMSAccountsDataQuery();
  const { data: quizzesData = [], isLoading: areQuizzesLoading } = useGetQuizzesDataQuery();

  const [updateSchool, { isLoading: isSchoolUpdating }] = useUpdateSchoolDataMutation();

  const {
    name,
    format,
    icon,
    description,
    format_description: formatDescription,
    short_description: shortDescription,
    lms_account_id: initialLMSAccountId,
    duration,
    cities,
    poll: initialPoll,
    test: initialTest,
    id: schoolId,
    active: isActive,
    logo: initialLogo,
    certification: hasCertificate,
  } = schoolData;

  const methods = useForm<TPublicSchoolFormData>({
    defaultValues: {
      title: name ?? '',
      format: format ?? ESchoolFormat.Online,
      description: description ?? '',
      icon: icon ?? '',
      shortDescFirst: shortDescription[0] ?? '',
      shortDescSecond: shortDescription[1] ?? '',
      shortDescThird: shortDescription[2] ?? '',
      shortDescFourth: shortDescription[3] ?? '',
      onlineDescription: format === ESchoolFormat.Online ? formatDescription : '',
      offlineDescription: format === ESchoolFormat.Offline ? formatDescription : '',
      hybridDescription: format === ESchoolFormat.Hybrid ? formatDescription : '',
      logo: initialLogo ?? null,
      lmsAccountId: initialLMSAccountId ?? null,
      duration: duration ? String(duration) : '',
      certification: hasCertificate,
      cityIds: cities ? cities.map(({ id }) => id) : [],
      pollId: initialPoll ? Number(initialPoll.external_id) : null,
      testId: initialTest ? Number(initialTest.external_id) : null,
    },
    resolver: yupResolver(PUBLIC_SCHOOL_VALIDATION_SCHEMA),
  });

  const { watch, handleSubmit, setValue, clearErrors } = methods;

  const [numberOfShortDescInputs, setNumberOfShortDescInputs] = useState(shortDescription.length || 1);

  const [isImageChanged, setIsImageChanged] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const lmsAccountId = watch('lmsAccountId');
  const pollId = watch('pollId');
  const testId = watch('testId');
  const logo = watch('logo');
  watch((_, { name }) => {
    if (name === 'lmsAccountId') {
      setValue('pollId', null);
      setValue('testId', null);
    }
  });
  const currentFormat = watch('format');

  const citiesList = useMemo(() => citiesData.map(({ id, name }) => ({ value: id, label: name })), [citiesData]);
  const badgeIconsList = useMemo(
    () =>
      Object.values(ESchoolBadgeIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );
  const lmsAccountsList = useMemo(
    () => lmsAccountsData.map(({ id, name }) => ({ value: id, label: name })),
    [lmsAccountsData],
  );
  const quizzesList = useMemo(
    () =>
      quizzesData.reduce(
        (prevVal, { account_id, id, name }) => {
          if (account_id === lmsAccountId) {
            return [...prevVal, { value: id, label: name }];
          }

          return prevVal;
        },
        [] as { value: number; label: string }[],
      ),
    [quizzesData, lmsAccountId],
  );

  const addShortDescField = useCallback(() => setNumberOfShortDescInputs((prevValue) => prevValue + 1), []);
  const removeShortDescField = useCallback((fieldValue: 'shortDescSecond' | 'shortDescThird' | 'shortDescFourth') => {
    setNumberOfShortDescInputs((prevValue) => prevValue - 1);
    setValue(fieldValue, '');
  }, []);

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`);
  };

  const getImageName = () => {
    if (!logo) {
      return '';
    }

    return logo.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setValue('logo', null);
    setImageUrl('');
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  const imageChangeCallback = useCallback(() => setIsImageChanged(true), []);

  const getPollData = () => {
    const poll = quizzesList.find(({ value }) => value === pollId);

    if (!poll) {
      return null;
    }

    if (pollId === Number(initialPoll?.external_id)) {
      return initialPoll;
    }

    return { title: poll.label, external_id: String(poll.value), quiz_type: EQuizType.Poll };
  };

  const getTestData = () => {
    const test = quizzesList.find(({ value }) => value === testId);

    if (!test) {
      return null;
    }

    if (testId === Number(initialTest?.external_id)) {
      return initialTest;
    }

    return { title: test.label, external_id: String(test.value), quiz_type: EQuizType.Test };
  };

  const publishSchool = async () => {
    await sendApiRequest({
      successMessage: 'Школа опубликована',
      successCallback: () => void navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`),
      apiRequestCallback: () =>
        updateSchool({
          id: Number(schoolId),
          data: {
            active: true,
          },
        }),
    });
  };

  const saveSchoolData = handleSubmit(
    async ({
      title,
      format,
      description,
      icon,
      duration,
      logo,
      cityIds,
      shortDescFirst,
      shortDescSecond,
      shortDescThird,
      shortDescFourth,
      lmsAccountId,
      onlineDescription,
      offlineDescription,
      hybridDescription,
      certification,
    }) => {
      if (!lmsAccountId || !logo) {
        return;
      }

      const shortDescription = [shortDescFirst, shortDescSecond, shortDescThird, shortDescFourth].filter(
        (desc): desc is string => Boolean(desc),
      );

      const FORMAT_TO_DESCRIPTION_MAPPER = {
        [ESchoolFormat.Online]: onlineDescription,
        [ESchoolFormat.Offline]: offlineDescription,
        [ESchoolFormat.Hybrid]: hybridDescription,
      };

      const data = {
        name: title,
        format,
        format_description: FORMAT_TO_DESCRIPTION_MAPPER[format],
        certification,
        description,
        icon,
        short_description: shortDescription,
        lms_account_id: lmsAccountId,
        active: false,
        duration: duration ? Number(duration) : null,
        city_ids: cityIds,
        poll: getPollData(),
        test: getTestData(),
        ...(logo.signed_id ? { logo: logo.signed_id } : {}),
      };

      await sendApiRequest({
        successMessage: 'Школа успешно обновлена',
        apiRequestCallback: () =>
          updateSchool({
            id: schoolId,
            data,
          }),
      });
    },
  );

  useEffect(() => {
    if (!logo?.url) {
      return;
    }

    if (!isImageChanged) {
      setImageUrl(logo.url);
    }
  }, [isImageChanged, logo]);

  useLayoutEffect(() => {
    if (isActive) {
      navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.VIEW}`);
    }
  }, [isActive, schoolId]);

  useEffect(() => {
    clearErrors(['onlineDescription', 'offlineDescription', 'hybridDescription']);
  }, [currentFormat]);

  return (
    <div className={styles['content-wrapper']}>
      <FormProvider {...methods}>
        <DataSection
          titleText="Название"
          isTitleRequired
        >
          <InputField
            fieldName="title"
            placeholder="Название"
          />
        </DataSection>
        <DataSection
          titleText="Изображение школы"
          isTitleRequired
        >
          <ImageUploadField
            accept="image/png,image/jpeg,image/jpg"
            name="Изображение"
            fieldName="logo"
            className={logo ? styles['image-uploaded'] : ''}
            requestCallback={imageRequestCallback}
            changeCallback={imageChangeCallback}
            showUploadList={false}
          />
          {logo && (
            <>
              <Button
                size="small"
                onClick={() => setIsImagePreviewOpen(true)}
                type="text"
                icon={<PaperClipOutlined />}
                className={styles['image-button-wrapper']}
              >
                {getImageName()}
                <DeleteOutlined
                  className={styles['remove-image-icon']}
                  onClick={removeUploadedImage}
                />
              </Button>
              <Image
                alt="Лого школы"
                style={{ display: 'none' }}
                preview={{
                  open: isImagePreviewOpen,
                  src: imageUrl,
                  onOpenChange: (value) => setIsImagePreviewOpen(value),
                }}
              />
            </>
          )}
        </DataSection>
        <DataSection titleText="Иконка школы">
          <SelectField
            fieldName="icon"
            size="large"
            virtual={false}
            showSearch={false}
            placeholder="Выбрать иконку"
            options={badgeIconsList}
          />
        </DataSection>
        <DataSection titleText="Краткое описание школы">
          {Array.from({ length: numberOfShortDescInputs }).map((_, index) => {
            const fieldName = SHORT_DESC_INDEX_TO_FIELD_NAME_MAPPER[index];

            if (index === 0) {
              return (
                <InputField
                  key={fieldName}
                  fieldName={fieldName}
                  placeholder="Краткое описание школы"
                />
              );
            }

            return (
              <div
                className={styles['short-desc-input-wrapper']}
                key={fieldName}
              >
                <InputField
                  fieldName={fieldName}
                  placeholder="Краткое описание школы"
                />
                <Button
                  onClick={() =>
                    removeShortDescField(fieldName as 'shortDescSecond' | 'shortDescThird' | 'shortDescFourth')
                  }
                  type="dashed"
                  size="middle"
                  className={styles.button}
                  icon={<CloseOutlined />}
                />
              </div>
            );
          })}
          {numberOfShortDescInputs < 4 && (
            <Button
              onClick={addShortDescField}
              type="dashed"
              size="middle"
              className={styles.button}
              icon={<PlusOutlined />}
            >
              Добавить описание
            </Button>
          )}
        </DataSection>
        <DataSection titleText="Полное описание школы">
          <TextAreaField
            fieldName="description"
            placeholder="Полное описание школы"
          />
        </DataSection>
        <DataSection titleText="Длительность школы, месяцев">
          <InputField
            fieldName="duration"
            placeholder="Длительность школы, месяцев"
            style={{ width: '100%' }}
          />
        </DataSection>
        <DataSection titleText="Сертификат">
          <CheckboxField fieldName="certification">Показывать блок “Сертификат” на странице школы</CheckboxField>
        </DataSection>
        <div className={styles['half-width-section-wrapper']}>
          <DataSection
            titleText="Города школы"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="cityIds"
              mode="multiple"
              size="large"
              placeholder="Выбрать город"
              options={citiesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areCitiesLoading}
              disabled={areCitiesLoading}
              notFoundContent="По запросу ничего не найдено"
            />
          </DataSection>
          <DataSection
            titleText="Аккаунт школы в LMS"
            wrapperClassName={styles['half-width-section']}
            isTitleRequired
          >
            <SelectField
              fieldName="lmsAccountId"
              size="large"
              placeholder="Выбрать аккаунт"
              options={lmsAccountsList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areLMSAccountsLoading}
              disabled={areLMSAccountsLoading}
            />
          </DataSection>
        </div>
        <div className={styles['half-width-section-wrapper']}>
          <DataSection
            titleText="Опрос"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="pollId"
              size="large"
              placeholder="Выбрать опрос"
              options={quizzesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areQuizzesLoading}
              disabled={areQuizzesLoading || !lmsAccountId}
            />
          </DataSection>
          <DataSection
            titleText="Тестовое задание"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="testId"
              size="large"
              placeholder="Выбрать тестовое задание"
              options={quizzesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areQuizzesLoading}
              disabled={areQuizzesLoading || !lmsAccountId}
            />
          </DataSection>
        </div>
        <p className={styles['surveys-annotation']}>
          После создания школы в LMS куратор сможет создать опросы и тестовые задания, которые затем появятся в этом
          списке для выбора к текущей школе.
        </p>
        <DataSection titleText="Формат">
          <Controller
            name="format"
            render={({ field }) => (
              <Radio.Group
                {...field}
                className={styles['format-wrapper']}
                optionType="default"
              >
                {SCHOOL_FORMAT_RADIO_GROUP_DATA.map(
                  ({ id, data: { format, buttonText, inputTitle, fieldName, inputPlaceholder } }) => (
                    <div
                      key={id}
                      className={styles['format-radio-button-wrapper']}
                    >
                      <Radio.Button value={format}>{buttonText}</Radio.Button>
                      <DataSection titleText={inputTitle}>
                        <InputField
                          fieldName={fieldName}
                          placeholder={inputPlaceholder}
                          disabled={currentFormat !== format}
                        />
                      </DataSection>
                    </div>
                  ),
                )}
              </Radio.Group>
            )}
          />
        </DataSection>
        <div className={styles['subsections-wrapper']}>
          <NavigationLink
            title="Вопросы и ответы"
            url={ROUTES.SCHOOL_FAQ}
          />
          <NavigationLink
            title="Программа школы"
            url={ROUTES.SCHOOL_PROGRAM}
          />
          <NavigationLink
            title="Спикеры"
            url={ROUTES.SCHOOL_SPEAKERS}
          />
          <NavigationLink
            title="Чему вы научитесь"
            url={ROUTES.SCHOOL_ABILITIES}
          />
        </div>
        <div className={styles['buttons-wrapper']}>
          <Button
            onClick={goBack}
            type="dashed"
            size="large"
            className={styles.button}
          >
            Назад
          </Button>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Button
              onClick={saveSchoolData}
              type="default"
              size="large"
              className={styles['save-button']}
              disabled={isSchoolUpdating}
            >
              Сохранить
            </Button>
            <Button
              onClick={publishSchool}
              type="primary"
              size="large"
              className={styles['publish-button']}
              disabled={isSchoolUpdating}
            >
              Опубликовать
            </Button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
